/**
 * Links into PostHog from the portal. One builder, shared by every page that
 * offers "Recordings" (Individuals now; sites/sandboxes and agencies next), so
 * the filter and the encoding live in one place.
 *
 * Host and project come from config (VITE_POSTHOG_HOST / VITE_POSTHOG_PROJECT_ID),
 * not from the pages. The link carries the person's email, so only render it
 * for staff with read access to accounts.
 */
export interface PostHogConfig { host: string; projectId: string }

export const POSTHOG_CONFIG: PostHogConfig = {
  host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.posthog.com',
  projectId: import.meta.env.VITE_POSTHOG_PROJECT_ID || '284078',
};

/**
 * The recording property the site is matched on: PostHog's "Visited page" replay
 * filter (a recording property, matched on every page URL of the session). One
 * constant so it is a one-line change if the PostHog-side ticket adds a custom
 * `site_url` property that matches better.
 */
export const SITE_FILTER_KEY = 'visited_page';

/** "https://Acme.try.example.com/wp-admin/" -> "acme.try.example.com"; null when empty. */
export function siteHost(urlOrHost: string | null | undefined): string | null {
  const h = (urlOrHost || '').trim().replace(/^[a-z]+:\/\//i, '').split(/[/?#]/)[0].toLowerCase();
  return h || null;
}

/**
 * Longest Session replay URL we hand out. The filters ride in the query string,
 * and proxies in front of PostHog and of the browser start refusing around 8 KB.
 */
export const MAX_URL_LENGTH = 6000;

/** One email, several (matched as "is one of", i.e. OR), or none. */
export type Who = string | string[] | null | undefined;

const emailsOf = (who: Who): string[] =>
  [...new Set((Array.isArray(who) ? who : [who]).map((e) => (e || '').trim()).filter(Boolean))];

/**
 * The Session replay filter, last 30 days, > 5 s active, newest first, for a
 * person by email (several emails = any of them) and/or a site's host.
 */
export function recordingsFilters(who: Who, site?: string | null) {
  const emails = emailsOf(who);
  const host = siteHost(site);
  const conditions: Array<Record<string, unknown>> = [];
  if (emails.length) {
    conditions.push({ key: 'email', value: emails.length === 1 ? emails[0] : emails, operator: 'exact', type: 'person' });
  }
  if (host) conditions.push({ key: SITE_FILTER_KEY, value: host, operator: 'icontains', type: 'recording' });
  return {
    filter_test_accounts: false,
    date_from: '-30d',
    date_to: null,
    filter_group: { type: 'AND', values: [{ type: 'AND', values: conditions }] },
    duration: [{ type: 'recording', key: 'active_seconds', value: 5, operator: 'gt' }],
    order: 'start_time',
    order_direction: 'DESC',
  };
}

const build = (who: Who, site: string | null | undefined, cfg: PostHogConfig) =>
  `${cfg.host.replace(/\/+$/, '')}/project/${encodeURIComponent(cfg.projectId)}/replay/home?filters=` +
  encodeURIComponent(JSON.stringify(recordingsFilters(who, site)));

/** Does filtering this site by all of these emails still fit in one URL? */
export function emailsFit(who: Who, site?: string | null, cfg: PostHogConfig = POSTHOG_CONFIG): boolean {
  return build(who, site, cfg).length <= MAX_URL_LENGTH;
}

/**
 * PostHog Session replay for a person (by email), a site (by URL or host), or
 * both; null when there is neither. With a site, more emails than fit in
 * MAX_URL_LENGTH fall back to the site alone (every visitor of that site),
 * until PostHog carries a group/agency property to filter on instead; without
 * a site the list is trimmed to the emails that fit.
 */
export function recordingsUrl(
  who: Who,
  opts: { site?: string | null; cfg?: PostHogConfig } = {},
): string | null {
  const cfg = opts.cfg ?? POSTHOG_CONFIG;
  const emails = emailsOf(who);
  const site = siteHost(opts.site) ? opts.site : null;
  if (!emails.length && !site) return null;
  const url = build(emails, site, cfg);
  if (url.length <= MAX_URL_LENGTH || emails.length < 2) return url;
  if (site) return build([], site, cfg);
  const kept = [...emails];
  while (kept.length > 1 && build(kept, null, cfg).length > MAX_URL_LENGTH) kept.pop();
  return build(kept, null, cfg);
}
